/**
 * What the ticks on one of my own messages should say.
 *
 * A pure function of the message and the member list so that the bubble and
 * the message-info panel cannot disagree, and so it can be tested without a
 * thread.
 *
 *   pending    a clock   - sent from here, not yet stored by the server
 *   failed     a warning - the server refused it or could not be reached
 *   sent       one tick  - stored, but has not reached everyone
 *   delivered  two ticks - has reached every recipient, not everyone has read it
 *   read       two blue  - every recipient has read it
 *
 * WhatsApp's rule for groups, and the one people already know: the ticks move
 * when the LAST person catches up, not the first. Who has and has not is what
 * the info panel is for.
 */

const idOf = (value) => String(value?._id || value || '');

/** Receipt lists only grow; a late upload response or cached snapshot cannot undo a live tick. */
export const preserveReceipts = (message, current) => {
    if (!current) return message;
    const combine = (saved = [], live = []) => {
        const byUser = new Map(saved.map(row => [idOf(row.user), row]));
        live.forEach(row => {
            const id = idOf(row.user);
            if (!byUser.has(id)) byUser.set(id, row);
        });
        return [...byUser.values()];
    };
    return {
        ...message,
        readBy: combine(message.readBy, current.readBy),
        deliveredTo: combine(message.deliveredTo, current.deliveredTo)
    };
};

/**
 * Who a message is addressed to: every member bar its sender, less anybody who
 * joined after it was sent — they were never sent it, and counting them would
 * hold the ticks grey forever for a message that has reached everyone it could.
 *
 * `members` is the list the server already filtered through visibleMembers, so
 * a hidden oversight admin is not in it and can never hold the ticks back.
 */
export const recipientsOf = (message, members, myId) => {
    const sentAt = message?.createdAt ? new Date(message.createdAt) : null;
    return (members || [])
        .filter((m) => {
            const id = idOf(m.user);
            if (!id || id === String(myId)) return false;
            if (m.leftAt) return false;
            if (sentAt && m.joinedAt && new Date(m.joinedAt) > sentAt) return false;
            return true;
        })
        .map((m) => idOf(m.user));
};

export const tickState = (message, members, myId) => {
    if (message?.failed) return 'failed';
    if (message?.pending) return 'pending';

    const recipients = recipientsOf(message, members, myId);
    if (!recipients.length) return 'sent';

    const read = new Set((message.readBy || []).map((r) => idOf(r.user)));
    // A read implies a delivery; the two lists are not kept in step.
    const reached = new Set([...read, ...(message.deliveredTo || []).map((r) => idOf(r.user))]);

    if (recipients.every((id) => read.has(id))) return 'read';
    if (recipients.every((id) => reached.has(id))) return 'delivered';
    return 'sent';
};

/**
 * Within this long of sending counts as "Immediately".
 *
 * A timestamp of 10:42 under a message sent at 10:42 says nothing a person can
 * use; "Immediately" says what they actually want to know — that it arrived
 * while they were still looking at it.
 */
export const IMMEDIATE_MS = 60 * 1000;

const time = (d) => d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

const sameDay = (a, b) => a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate();

/** "Today 14:05", "Yesterday 14:05", "12 Sep 14:05". */
export const stamp = (at, now = new Date()) => {
    const d = new Date(at);
    if (Number.isNaN(d.getTime())) return '';
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    if (sameDay(d, now)) return `Today ${time(d)}`;
    if (sameDay(d, yesterday)) return `Yesterday ${time(d)}`;
    return `${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} ${time(d)}`;
};

/**
 * When a receipt happened, as the info panel shows it: "Immediately" if within
 * a minute of sending, otherwise the time. null when it has not happened.
 */
export const receiptTime = (at, sentAt, now = new Date()) => {
    if (!at) return null;
    if (sentAt && new Date(at) - new Date(sentAt) < IMMEDIATE_MS) return 'Immediately';
    return stamp(at, now);
};
